/**
 * 用户状态管理（Zustand + 持久化）
 *
 * 职责：
 *   1. 管理用户登录状态、用户信息
 *   2. 自动持久化到 localStorage（使用 zustand persist 中间件）
 *   3. 与 WxAuthGuard、wx-callback 页面配合完成登录流程
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { WxUserInfo, WxWechatUser } from "@/api/wechat";

// 导出 rehydration 状态，供 useChatWebSocket 使用
export const userStoreRehydrated = { current: false };

// ─── 开发环境：强制使用写死的 Token ──────────────────
const DEV_TOKEN = "eyJ1aWQiOjI0LCJhY2NvdW50IjoidGVzdCIsInBob25lIjoiMTc3MTA4NjE2MDQiLCJ0eXBlIjoidXNlciIsInRpbWUiOjE3ODAxMDk5NDN9";
if (import.meta.env.DEV && typeof window !== "undefined") {
  // 同步写入 supplier_token（供 peiwantHttp 认证）
  localStorage.setItem("supplier_token", DEV_TOKEN);
  // 删除旧的持久化缓存，让 persist 从默认状态（含新 DEV_TOKEN）开始
  localStorage.removeItem("user-storage");
}

interface UserState {
  // ─── 登录状态 ───────────────────────────────────

  /** 是否已登录 */
  isLoggedIn: boolean;
  /** 认证 Token */
  token: string | null;
  /** Token 类型 */
  tokenType: string;
  /** Token 过期时间戳 */
  tokenExpiresAt: number | null;

  // ─── 用户信息 ───────────────────────────────────

  /** 平台用户信息 */
  userInfo: WxUserInfo | null;

  /** 是否达人（1=是，0=否） */
  isTalent: number;
  /** 微信用户信息 */
  wechatUser: WxWechatUser | null;

  // ─── Actions ───────────────────────────────────

  /** 登录成功，设置用户数据 */
  login: (data: {
    token: string;
    tokenType?: string;
    expiresIn?: number;
    user: WxUserInfo;
    wechatUser: WxWechatUser;
  }) => void;

  /** 退出登录 */
  logout: () => void;

  /** 更新用户信息 */
  updateUserInfo: (userInfo: Partial<WxUserInfo>) => void;

  /** 设置达人身份 */
  setIsTalent: (isTalent: number) => void;

  /** 检查 Token 是否过期 */
  isTokenExpired: () => boolean;

  /**
   * [开发调试] 手动设置 Token（仅开发环境 + 非微信环境可用）
   * 用法：浏览器访问 ?dev_token=xxx 或控制台调用
   */
  devSetToken: (token: string) => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => {
      return {
      // 初始状态：仅开发环境写死 token 用于调试，线上为空需走登录流程
      isLoggedIn: import.meta.env.DEV,
      token: import.meta.env.DEV ? "eyJ1aWQiOjI0LCJhY2NvdW50IjoidGVzdCIsInBob25lIjoiMTc3MTA4NjE2MDQiLCJ0eXBlIjoidXNlciIsInRpbWUiOjE3ODAxMDk5NDN9" : null,
      tokenType: "Bearer",
      tokenExpiresAt: Date.now() + 86400 * 7 * 1000, // 默认7天
      userInfo: null,
      wechatUser: null,
      isTalent: 0,

      // 登录
      login: (data) => {
        const expiresAt = data.expiresIn
          ? Date.now() + data.expiresIn * 1000
          : null;

        set({
          isLoggedIn: true,
          token: data.token,
          tokenType: data.tokenType || "Bearer",
          tokenExpiresAt: expiresAt,
          userInfo: data.user,
          wechatUser: data.wechatUser,
          // ★ 新账号登录 → 重置达人身份，避免上个账号残留 isTalent=1 误显示工作台
          //   （真实身份由 TalentPrefetch / WxAuthGuard / 个人中心调接口后写入）
          isTalent: 0,
        });
        // 清除上个账号的达人缓存，防止串号
        localStorage.removeItem("is_talent_cache");
      },

      // 退出登录
      logout: () => {
        set({
          isLoggedIn: false,
          token: null,
          tokenType: "Bearer",
          tokenExpiresAt: null,
          userInfo: null,
          wechatUser: null,
          isTalent: 0,
        });
        // 清除独立缓存，防止串号
        localStorage.removeItem("supplier_token");
        localStorage.removeItem("is_talent_cache");
        // 清除首页选中城市，避免下一个账号看到上个账号选的地址
        localStorage.removeItem("home_selected_city");
      },

      // 更新用户信息
      updateUserInfo: (partialInfo) => {
        const { userInfo } = get();
        if (!userInfo) {
          console.warn("[updateUserInfo] 用户未登录，无法更新信息");
          return;
        }
        set({
          userInfo: { ...userInfo, ...partialInfo },
        });
      },

      // 设置达人身份
      setIsTalent: (isTalent) => {
        set({ isTalent });
      },

      // 检查 Token 是否过期
      isTokenExpired: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return false;
        return Date.now() > tokenExpiresAt;
      },

      // [开发调试] 手动设置 Token（仅开发环境 + 非微信浏览器）
      devSetToken: (token: string) => {
        if (import.meta.env.PROD) {
          console.error("[devSetToken] 生产环境不可用");
          return;
        }
        if (/MicroMessenger/i.test(navigator.userAgent)) {
          console.warn("[devSetToken] 微信环境内不可用，请走正常授权流程");
          return;
        }
        set({
          isLoggedIn: true,
          token,
          tokenType: "Bearer",
          tokenExpiresAt: Date.now() + 86400 * 7 * 1000, // 默认7天
          userInfo: {
            uid: -1,
            account: "dev_user",
            nickname: "开发用户",
            avatar: "",
            phone: "",
            real_name: "",
            status: 1,
            is_apead: 0,
            now_money: "0",
            add_time: "",
          }, // uid=-1 标识为调试账号
          wechatUser: null,
        });
        // 同步写入 supplier_token，供 peiwantHttp 认证
        localStorage.setItem("supplier_token", token);
        console.log("[devSetToken] 已设置测试 token，刷新后仍有效");
      },
      };
    },
    {
      name: "user-storage", // localStorage 的 key
      version: 2, // 版本升级，丢弃旧缓存（修复 token 末尾 === 问题）
      storage: createJSONStorage(() => localStorage),
      // 只持久化这些字段
      partialize: (state) => ({
        isLoggedIn: state.isLoggedIn,
        token: state.token,
        tokenType: state.tokenType,
        tokenExpiresAt: state.tokenExpiresAt,
        userInfo: state.userInfo,
        wechatUser: state.wechatUser,
        isTalent: state.isTalent,
      }),
      // 数据恢复完成后标记
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!error && state) {
            userStoreRehydrated.current = true;
          }
        };
      },
    },
  ),
);

export default useUserStore;
